import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { PRIMARYCOLOR } from '../../Constants';

const BRAND = PRIMARYCOLOR || '#005CC5';

const settingsItems = [
  {
    label: 'Account',
    path: '/settings/account',
    description: 'Security and personal preferences',
    icon: AccountCircleOutlinedIcon,
  },
  {
    label: 'Team',
    path: '/settings/team',
    description: 'Members, roles, and team access',
    icon: GroupsOutlinedIcon,
  },
];

export default function SettingsLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: 6 }}>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 5,
          border: `1px solid ${alpha(BRAND, 0.14)}`,
          bgcolor: '#ffffff',
          boxShadow: `0 18px 42px ${alpha(BRAND, 0.08)}`,
        }}
      >
        <Box aria-hidden sx={{ position: 'absolute', inset: '0 0 auto', height: 6, bgcolor: BRAND }} />
        <Box aria-hidden sx={{ position: 'absolute', top: -105, right: -20, width: 300, height: 300, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(BRAND, 0.14)} 0%, rgba(255,255,255,0) 72%)` }} />
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          sx={{ position: 'relative', p: { xs: 2.25, md: 3 } }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: alpha(BRAND, 0.09), color: BRAND, border: `1px solid ${alpha(BRAND, 0.14)}` }}>
              <SettingsOutlinedIcon />
            </Box>
            <Box>
              <Typography sx={{ color: BRAND, fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                Workspace Settings
              </Typography>
              <Typography variant="h4" sx={{ mt: 0.35, fontWeight: 950, color: '#0f172a', letterSpacing: '-0.025em' }}>
                Settings
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.4, color: alpha('#0f172a', 0.66), lineHeight: 1.6 }}>
                Manage your sign-in security, team access, and billing preferences.
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<AccountBalanceWalletOutlinedIcon />}
            onClick={() => navigate('/billing')}
            sx={{ borderRadius: 3, px: 2, py: 1, textTransform: 'none', fontWeight: 850, bgcolor: '#fff' }}
          >
            Billing & Credits
          </Button>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ mt: 2, p: 1, borderRadius: 3.5, border: `1px solid ${alpha(BRAND, 0.12)}`, bgcolor: '#fff' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {settingsItems.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                startIcon={<Icon />}
                aria-current={active ? 'page' : undefined}
                sx={{
                  flex: 1,
                  justifyContent: 'flex-start',
                  minHeight: 56,
                  px: 2,
                  borderRadius: 2.75,
                  textTransform: 'none',
                  color: active ? '#fff' : '#203854',
                  bgcolor: active ? BRAND : 'transparent',
                  '&:hover': { bgcolor: active ? BRAND : alpha(BRAND, 0.06) },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                  <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }}>{item.label}</Typography>
                    <Typography variant="caption" sx={{ color: active ? alpha('#fff', 0.78) : '#708095' }}>{item.description}</Typography>
                  </Box>
                  {active ? <Chip size="small" label="Active" sx={{ ml: 'auto', bgcolor: alpha('#fff', 0.16), color: '#fff', fontWeight: 800 }} /> : null}
                </Stack>
              </Button>
            );
          })}
        </Stack>
      </Paper>

      <Box sx={{ mt: 2.25 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
